import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for scroll-triggered animations
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Intersection threshold (0-1)
 * @param {string} options.rootMargin - Root margin for intersection observer
 * @param {boolean} options.once - Whether to trigger animation only once
 * @returns {Object} - Ref and animation state
 */
export const useScrollAnimation = (options = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '0px 0px -50px 0px',
    once = true
  } = options;

  const elementRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) {
            setHasAnimated(true);
            observer.unobserve(element);
          }
        } else if (!once && !hasAnimated) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [threshold, rootMargin, once, hasAnimated]);

  return {
    ref: elementRef,
    isVisible,
    hasAnimated
  };
};

/**
 * Hook for staggered animations on multiple elements
 * @param {number} itemCount - Number of items to animate
 * @param {number} delay - Delay between each item animation
 * @returns {Object} - Animation controls
 */
export const useStaggerAnimation = (itemCount, delay = 100) => {
  const [animateIndex, setAnimateIndex] = useState(-1);
  const [isComplete, setIsComplete] = useState(false);

  const startAnimation = () => {
    setAnimateIndex(0);
    
    const animateNext = (index) => {
      if (index < itemCount) {
        setTimeout(() => {
          setAnimateIndex(index);
          animateNext(index + 1);
        }, delay);
      } else {
        setIsComplete(true);
      }
    };

    animateNext(0);
  };

  const resetAnimation = () => {
    setAnimateIndex(-1);
    setIsComplete(false);
  };

  const shouldAnimate = (index) => index <= animateIndex;

  return {
    startAnimation,
    resetAnimation,
    shouldAnimate,
    isComplete,
    animateIndex
  };
};

/**
 * Hook for counter animations
 * @param {number} end - End value for counter
 * @param {number} duration - Animation duration in milliseconds
 * @param {number} start - Start value (default: 0)
 * @returns {Object} - Counter value and controls
 */
export const useCounterAnimation = (end, duration = 2000, start = 0) => {
  const [count, setCount] = useState(start);
  const [isAnimating, setIsAnimating] = useState(false);

  const startCounter = () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    const startTime = Date.now();
    const range = end - start;

    const updateCounter = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = start + (range * easeOut);
      
      setCount(Math.floor(currentValue));

      if (progress < 1) {
        requestAnimationFrame(updateCounter);
      } else {
        setCount(end);
        setIsAnimating(false);
      }
    };

    requestAnimationFrame(updateCounter);
  };

  const resetCounter = () => {
    setCount(start);
    setIsAnimating(false);
  };

  return {
    count,
    startCounter,
    resetCounter,
    isAnimating
  };
};

/**
 * Hook for magnetic button effect
 * @param {number} strength - Magnetic strength (default: 0.3)
 * @returns {Object} - Event handlers and styles
 */
export const useMagneticEffect = (strength = 0.3) => {
  const [transform, setTransform] = useState('translate(0px, 0px)');
  const elementRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!elementRef.current) return;

    const rect = elementRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const deltaX = (e.clientX - centerX) * strength;
    const deltaY = (e.clientY - centerY) * strength;
    
    setTransform(`translate(${deltaX}px, ${deltaY}px)`);
  };

  const handleMouseLeave = () => {
    setTransform('translate(0px, 0px)');
  };

  return {
    ref: elementRef,
    style: { transform },
    onMouseMove: handleMouseMove,
    onMouseLeave: handleMouseLeave
  };
};

/**
 * Hook for parallax effect
 * @param {number} speed - Parallax speed (default: 0.5)
 * @returns {Object} - Ref and transform style
 */
export const useParallax = (speed = 0.5) => {
  const [transform, setTransform] = useState('translateY(0px)');
  const elementRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!elementRef.current) return;

      const rect = elementRef.current.getBoundingClientRect();
      const scrolled = window.pageYOffset;
      const rate = scrolled * -speed;
      
      setTransform(`translateY(${rate}px)`);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);

  return {
    ref: elementRef,
    style: { transform }
  };
};

export default useScrollAnimation;